"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { collection, addDoc, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";

export default function AnalyticsTracker() {
    const pathname = usePathname();
    const initialized = useRef(false);

    useEffect(() => {
        // Prevent tracking in admin or dev environment if needed, but for now we track everything except actual admin actions
        if (pathname?.startsWith("/admin")) return;

        // Simple debounce/dedupe to avoid double fires in React.StrictMode
        if (initialized.current) return;
        initialized.current = true;

        const trackView = async () => {
            try {
                // Generate or retrieve Session ID
                let sessionId = sessionStorage.getItem("analytics_session_id");
                if (!sessionId) {
                    sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
                        return v.toString(16);
                    });
                    sessionStorage.setItem("analytics_session_id", sessionId);
                }

                // Get browser info
                const userAgent = navigator.userAgent;
                const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
                const platform = navigator.platform;

                // Try to get country from free IP API (client-side only)
                let country = "Unknown";
                try {
                    const res = await fetch("https://ipapi.co/json/");
                    if (res.ok) {
                        const data = await res.json();
                        country = data.country_name || "Unknown";
                    }
                } catch (e) {
                    console.warn("Could not fetch location data");
                }

                // Ensure all fields have valid defined values so Firestore addDoc doesn't crash Turbopack Next.js apps
                const docData = {
                    type: "page_view",
                    path: pathname || "/",
                    sessionId: sessionId,
                    timestamp: new Date().toISOString(),
                    userAgent: userAgent || "unknown",
                    isMobile: isMobile || false,
                    platform: platform || "unknown",
                    country: country || "Unknown",
                    referrer: document.referrer || "Direct"
                };

                // Write to Firestore
                if (db) {
                     const coll = collection(db, "analytics_events");
                     await addDoc(coll, docData);
                }

            } catch (err) {
                console.error("Analytics Error:", err);
            }
        };

        trackView();

        // Reset init ref on unmount so navigation triggers it again? 
        // No, useEffect with pathname dependency handles navigation.
        // We need to handle the fact that React 18 strict mode double-invokes.
        // But with pathname as dependency, we need to allow re-runs on path change.

        return () => {
            initialized.current = false;
        };
    }, [pathname]);

    return null;
}
